<template>
  <div id="company_select" class="form_col">
    <span :class="titleClasses">{{ label || "Company" }}</span>
    <a-select
      ref="selectRef"
      :value="isMultiple ? formState.company_ids : selectedCompanyId"
      @update:value="handleValueUpdate"
      :placeholder="placeholder || 'Select Company'"
      show-search
      :mode="isMultiple ? 'multiple' : undefined"
      :default-active-first-option="true"
      :show-arrow="false"
      :filter-option="false"
      :not-found-content="customNotFoundContent"
      allow-clear
      @search="debouncedSearchCompanies"
      @change="handleCompanyChange"
      @dropdown-visible-change="handleDropdownVisibleChange"
      @clear="handleClear"
      @keyup.enter="handleEnterKey"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_white_arrow"
          class="size-4"
        />
      </template>
      <a-select-option
        v-for="company in mergedCompanies"
        :key="company.id"
        :value="company.id"
      >
        <div class="flex items-center space-x-2">
          <ProfileIcon
            :data="company"
            :size="16"
            :font="10"
            avatar="logo_url"
            param="name"
          />
          <span>{{
            getEntityDisplayName(company, companiesPrimaryAttrSlug) ||
            renderEntity(company) ||
            company.id
          }}</span>
        </div>
      </a-select-option>
      <template #dropdownRender="{ menuNode: menu }">
        <v-nodes
          :vnodes="menu"
          :entity-id="entityId"
          :form-state="formState"
          :is-editing="false"
          :visible="true"
        />
        <div
          v-if="searchQuery && hasCreateAccess"
          class="w-full text_color px-3 py-2 cursor-pointer"
          @mousedown.prevent="handleAddClick"
        >
          + Add "{{ searchQuery }}"
        </div>
      </template>
    </a-select>
  </div>
</template>

<script setup>
import { defineComponent, onMounted, ref, watch, computed } from "vue";
import { useStore } from "vuex";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import { message } from "ant-design-vue";
import { compact, debounce, uniqBy } from "lodash";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { renderEntity } from "../utils/utils.js";
import { getEntityDisplayName } from "@/src/utils/entityUtils.js";
import { transformCRMSearchResult } from "@/src/utils/crmFilterUtils.js";

import { imageNames } from "@/src/constants/imageConstants.js";

const store = useStore();

// Companies' primary attribute slug — may be renamed (e.g. "name" → "company_name")
// so we can't assume `attributes.name` exists. Pull it from the schema store the
// same way AssociationsContainer does; falls back to "name" if the schema isn't
// cached yet (first paint before the store hydrates).
const companiesPrimaryAttrSlug = computed(() => {
  const slug = store.getters["schema/getPrimaryAttributeSlugBySlug"](
    "companies",
    null,
    "crm",
  );
  return slug || "name";
});

// Pick the primary attribute's value out of an API record, handling both the
// wrapped ({ value: ... }) and flat shapes the backend returns.
const readPrimaryValue = (attrs) => {
  const primary = attrs?.[companiesPrimaryAttrSlug.value];
  if (primary == null) return null;
  if (typeof primary === "object") return primary.value ?? null;
  return primary;
};

const props = defineProps({
  leadId: { type: Number, required: false },
  contactId: { type: Number, required: false },
  companyId: { type: Number, required: false },
  dealId: { type: Number, required: false },
  formState: { type: Object, required: true },
  selectedCompanyId: { type: [String, Number], required: false },
  initialSelectedCompanies: { type: Array, required: false, default: [] },
  isMultiple: { type: Boolean, default: false },
  label: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  entityType: { type: String, default: "" },
  fieldName: { type: String, default: "current_company" }, // Allow parent to specify the field name
  titleClasses: { type: String, default: "" }, // Extra classes for title/label
});

const emit = defineEmits([
  "update:selected-company-id",
  "update:company-ids",
  "update:selected-company",
]);

const VNodes = defineComponent({
  props: {
    vnodes: { type: Object, required: true },
  },
  render() {
    return this.vnodes;
  },
});

const companies = ref([]);
const searchQuery = ref("");
const lastSearchQuery = ref(""); // Store the query before it gets cleared
const customNotFoundContent = ref("No companies found");
const isSearching = ref(false);
const selectRef = ref(null);

const entityId = computed(
  () => props.dealId || props.leadId || props.contactId
);

// Helper function to format company object consistently (DRY)
const formatCompanyObject = (company) => ({
  id: company.id,
  entity_type: "COMPANY",
  name: company.name || null,
  website: company.website || null,
  industry: company.industry || null,
  logo_url: company.logo_url || null,
  location: company.location || "",
});

const mergedCompanies = computed(() => {
  const searchActive = Boolean(searchQuery.value);
  const merged = uniqBy(
    compact([...companies.value, ...props.initialSelectedCompanies]),
    "id"
  );

  return searchActive ? companies.value : merged;
});

const hasCreateAccess = computed(() => {
  if (store.getters["rbac/isABACMode"]) {
    return store.getters["rbac/canPerformAction"]("companies", "CREATE");
  }
  return true;
});

const handleValueUpdate = (value) => {
  if (props.isMultiple) {
    props.formState.company_ids = value;
    emit("update:company-ids", value);
  } else {
    emit("update:selected-company-id", value);
    // Also emit the full company object for forms that need it
    if (value) {
      const selectedCompany = mergedCompanies.value.find((c) => c.id === value);
      if (selectedCompany) {
        emit("update:selected-company", formatCompanyObject(selectedCompany));
      }
    } else {
      emit("update:selected-company", null);
    }
  }
};

// handleCompanyChange is called by @change - avoid duplicate calls
let lastProcessedValue = null;
const handleCompanyChange = (value) => {
  if (value === lastProcessedValue) {
    return;
  }
  lastProcessedValue = value;
};

const handleClear = () => {
  if (props.isMultiple) {
    props.formState.company_ids = [];
    emit("update:company-ids", []);
  } else {
    props.formState[props.fieldName] = null;
    if (props.formState.company_ids) {
      props.formState.company_ids = [];
    }
    emit("update:selected-company-id", null);
  }
};

const searchCompanies = async (searchTerm = "") => {
  isSearching.value = true;
  searchQuery.value = searchTerm;
  // Save the search term for later use (before it gets cleared)
  if (searchTerm) {
    lastSearchQuery.value = searchTerm;
  }
  try {
    const response = await CRMService.searchDynamicObjects({
      object_type: 'companies',
      search_query: searchTerm,
      page: 1,
      page_size: 50,
    });
    if (response.success) {
      companies.value = (response.results || []).map((company) => {
        const attrs = company?.attributes || {};
        return {
          id: company?.id,
          name:
            readPrimaryValue(attrs) ||
            attrs?.name?.value ||
            attrs?.name ||
            null,
          website: attrs?.website?.value || attrs?.website || null,
          industry: attrs?.industry?.value || attrs?.industry || null,
          logo_url: attrs?.logo_url?.value || attrs?.logo_url || null,
          location: attrs?.location?.value || attrs?.location || null,
        };
      });
    } else {
      message.error("Failed to fetch companies");
    }
  } catch (error) {
    console.error("Error fetching companies:", error);
    message.error("An error occurred while fetching companies");
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearchCompanies = debounce(searchCompanies, 300);

const resetSearch = () => {
  searchQuery.value = "";
  if (props.isMultiple && selectRef.value?.blur) {
    selectRef.value.blur();
  }
};

const handleDropdownVisibleChange = (visible) => {
  if (!visible) {
    searchCompanies();
  }
};

const handleEnterKey = (e) => {
  // Use lastSearchQuery because searchQuery gets cleared by ant-design on Enter
  const queryToCheck = searchQuery.value || lastSearchQuery.value;
  
  // Only intercept if there's a search query and user wants to add new
  if (!queryToCheck || !hasCreateAccess.value) {
    return; // Let default behavior happen (select first option)
  }

  // Check if there's an exact match in existing companies
  const exactMatch = companies.value.find(
    (company) => company.name?.toLowerCase() === queryToCheck.toLowerCase()
  );
  
  // If no exact match, create new company
  if (!exactMatch) {
    // Restore the search query for handleAddClick
    searchQuery.value = queryToCheck;
    handleAddClick();
  }
};

const handleAddClick = async () => {
  if (isSearching.value || !searchQuery.value) return;

  const currentQuery = searchQuery.value;
  resetSearch();
  try {
    const payload = {
      name: currentQuery,
      ...(props.contactId && { contact_id: props.contactId }),
      ...(props.leadId && { lead_id: props.leadId }),
      ...(props.dealId && { deal_id: props.dealId }),
    };

    const response = await DashboardService.addCompany(payload);
    if (response.success) {
      setTimeout(() => {
        companies.value = [
          ...companies.value,
          {
            id: response.company.id,
            name: response.company.name,
            website: response.company.website,
          },
        ];

        if (props.isMultiple) {
          props.formState.company_ids = [
            ...(props.formState.company_ids || []),
            response.company.id,
          ];
        } else {
          emit("update:selected-company-id", response.company.id);
          // Also emit the full company object
          emit("update:selected-company", formatCompanyObject(response.company));
        }

        message.success("Company added successfully");
      }, 50);
    } else {
      message.error(
        "Failed to add company: " + (response.errors || "Unknown error")
      );
    }
  } catch (error) {
    console.error("Error adding company:", error);
    message.error("An error occurred while adding the company");
  }
};

watch(
  () => props.selectedCompanyId,
  (newId) => {
    if (!props.isMultiple && newId) {
      const foundCompany = companies.value.find(
        (company) => company.id === newId
      );
      if (foundCompany) {
        // Set the full company object in the proper format for the API
        props.formState[props.fieldName] = formatCompanyObject(foundCompany);
      }
    }
  }
);

onMounted(() => {
  // Warm the companies schema so companiesPrimaryAttrSlug resolves before
  // the first render picks up any stale "name" fallback.
  store.dispatch("schema/fetchSchema", { slug: "companies", service: "crm" });
  searchCompanies("", true);
});
</script>
